/**
 * Reusable "name and save" modal. Uses local styled components instead of
 * the shared `ConfirmModal` primitives — the legacy ones force ALL CAPS
 * on the heading and style the Cancel button identically to a primary
 * action, both of which break the design system. Local components keep
 * the changes scoped to this modal without affecting every other consumer
 * of `ConfirmModal`.
 *
 * Used by KaraokeSaveOrCancelTemplate (caption templates) and
 * CompositionSaveTemplate.
 */

import Modal from '@src/components/common/Modal';
import React, { useState } from 'react';
import styled from 'styled-components';

import type { ChangeEvent } from 'react';

type Props = {
  closeModal: () => void;
  onConfirm: (name: string) => void;
  onCancel: () => void;
  confirmText: React.ReactNode;
  cancelText: React.ReactNode;
  headingText?: React.ReactNode;
  /**
   * @deprecated The modal now uses design-system tokens for colour. The
   * prop is kept on the type for source compatibility with existing
   * callers but ignored at render time.
   */
  accentColor?: string;
  initialName: string;
};

export const SaveNameModal: React.FC<Props> = ({
  closeModal,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  headingText = 'Save',
  initialName,
}: Props) => {
  const [name, setName] = useState(initialName);

  const handleConfirm = () => {
    onConfirm(name);
    setName('');
  };

  return (
    <Modal
      isOpen
      closeModal={closeModal}
      backgroundColor="rgba(0, 0, 0, 0.75)"
      // The shared `Modal` wraps children in an unsized inner div. Pushing
      // the width onto `contentStyle` constrains that inner div directly so
      // the modal doesn't stretch toward the viewport edges.
      contentStyle={{
        width: '490px',
        maxWidth: '90vw',
        boxSizing: 'border-box',
      }}
    >
      <SModalWrapper>
        <SHeading>{headingText}</SHeading>
        <SFieldGroup>
          <SLabel htmlFor="save-name-modal-input">Template Name</SLabel>
          <SInput
            id="save-name-modal-input"
            placeholder="e.g. Bold yellow caption"
            value={name}
            autoFocus
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
            onKeyDown={e => {
              if (e.key === 'Enter' && name) handleConfirm();
            }}
          />
        </SFieldGroup>
        <SActions>
          <SCancelBtn type="button" onClick={onCancel}>
            {cancelText}
          </SCancelBtn>
          <SConfirmBtn
            type="button"
            onClick={handleConfirm}
            disabled={!name}
            {...(!name
              ? {
                  'aria-label': 'Name is required',
                  title: 'Name is required',
                }
              : {})}
          >
            {confirmText}
          </SConfirmBtn>
        </SActions>
      </SModalWrapper>
    </Modal>
  );
};

const SModalWrapper = styled.div`
  /*
   * Width is owned by the parent <Modal contentStyle={…}> — defining it
   * here too would create two competing sources of truth in DevTools.
   * Inherit the 490px / 90vw the parent sets.
   */
  width: 100%;
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
  padding: var(--spacing-2xl) var(--spacing-lg);
  box-sizing: border-box;
  background: var(--background);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const SHeading = styled.h2`
  margin: 0;
  color: var(--foreground);
  text-align: center;
  font-family: var(--font-primary);
  font-size: var(--text-md);
  font-weight: var(--font-weight-semi-bold);
  line-height: normal;
`;

const SFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const SLabel = styled.label`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
`;

const SInput = styled.input`
  /*
   * Height pinned to a single px value with box-sizing + line-height +
   * explicit min-height to defeat any global input cascade in this
   * codebase that was inflating the input box well past the token's
   * 40 px (the input was rendering at ~80 px before this).
   */
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  min-height: 44px;
  line-height: 1.2;
  padding: 0 var(--spacing-md);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  outline: none;
  transition: border-color 120ms ease;
  &:focus {
    /*
     * Accent-coloured border on focus, no halo. The previous box-shadow
     * spread read as a thick glow at the user's render scale.
     */
    border-color: var(--accent);
  }
  &::placeholder {
    color: var(--text-muted);
  }
`;

const SActions = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
`;

const SCancelBtn = styled.button`
  height: var(--btn-h-md);
  padding: 0 var(--btn-px-md);
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--foreground);
  border: 1px solid var(--border);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    color 120ms ease;
  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SConfirmBtn = styled(SCancelBtn)`
  background: var(--primary);
  color: var(--primary-foreground);
  border: none;
  font-weight: var(--font-weight-semi-bold);
  &:hover:not(:disabled) {
    background: var(--primary-hover);
    border-color: transparent;
  }
  &:active:not(:disabled) {
    background: var(--primary-pressed);
  }
`;
